// Ruta: src/components/ui/CallToActionBanner.tsx (Versión Full-Width, Sin Grid, Botón Texto Blanco)

import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "./Button";
import { trackSignupClick } from "../../utils/analytics";

type CallToActionBannerProps = React.HTMLAttributes<HTMLDivElement>;

const CallToActionBanner: React.FC<CallToActionBannerProps> = ({
  className,
  style,
  ...rest
}) => {
  const { t, i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: -1000, y: -1000 });
  const navigate = useNavigate();
  const langPrefix = `/${i18n.language}`;

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setMousePosition({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: -1000, y: -1000 });
  };

  const backgroundColor = "#ecfffb";

  const highlightColor = "rgba(0, 233, 190, 0.15)";
  const glassBgColor = "rgba(255, 255, 255, 0.15)";
  const glassBorderColor = "rgba(255, 255, 255, 0.25)";
  const textColor = "#005a50";

  return (
    <div
      ref={containerRef}
      {...rest}
      className={`relative overflow-hidden ${className || ""}`}
      style={{
        ...style,
        backgroundColor: backgroundColor,
        backgroundImage: `
          radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, ${highlightColor} 0%, transparent 40%)
        `,
        backgroundSize: `100% 100%`,
        transition: "background-position 0.1s ease-out",
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 md:py-12 lg:py-16" // Padding vertical (py) y horizontal (px) + centrado
      >
        <div
          className="relative rounded-2xl md:rounded-3xl border shadow-xl backdrop-blur-lg overflow-hidden" // overflow-hidden en la tarjeta glass
          style={{
            backgroundColor: glassBgColor,
            borderColor: glassBorderColor,
          }}
        >
          {/* Contenido Principal con padding interno */}
          <div className="p-8 md:p-10 lg:p-12">
            {" "}
            {/* Ajustado padding interno de la tarjeta */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 md:gap-10 text-center lg:text-left">
              {/* Sección de Texto */}
              <div className="flex-grow mb-6 lg:mb-0">
                <h2
                  className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 leading-tight" // Ajustado tamaño de fuente responsivo
                  style={{ color: textColor }}
                >
                  {t("ctaBanner.title", "Discover your Resonance")}
                </h2>
                <p
                  className="text-base sm:text-lg md:text-xl opacity-90 max-w-xl mx-auto lg:mx-0" // Ajustado tamaño de fuente responsivo
                  style={{ color: textColor }}
                >
                  {t(
                    "ctaBanner.description",
                    "Turning the unmet needs of your customers into a win for everyone starts now",
                  )}
                </p>
              </div>

              {/* Sección del Botón */}
              <div className="flex-shrink-0 w-full sm:w-auto">
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full sm:w-auto transition-shadow duration-300 shadow-md hover:shadow-lg"
                  onClick={() => {
                    trackSignupClick(i18n.language, "cta_banner");
                    navigate(`${langPrefix}/signup`);
                  }}
                  style={{
                    color: "#032f27ff",
                    paddingLeft: "2rem",
                    paddingRight: "2rem",
                  }}
                >
                  {t("ctaBanner.buttonText", "REQUEST A BRAINSTORM")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallToActionBanner;
